import Tooltip from '@katalon-studio/katalon-ui/Tooltip';
import PersonIcon from '@mui/icons-material/Person';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import { useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { fromAuth } from '../../../store';
import { ReactComponent as ViewProfileIcon } from '../../icons/ViewProfileIcon.svg';
import { ReactComponent as CommunityIcon } from '../../icons/CommunityIcon.svg';
import { ReactComponent as SignOutIcon } from '../../icons/SignOutIcon.svg';
import { ReactComponent as SubmitTicketIcon } from '../../icons/SubmitTicketIcon.svg';
import { logout } from '../../../routes';
import { getUserAvatar } from '../../../utils/Users';
import { MenuListDecorator } from './MenuListDecorator';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    height: theme.spacing(7.5),
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  settingButton: {
    margin: theme.spacing(1),
    width: theme.spacing(5),
    height: theme.spacing(5),
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.12)',
    },
  },
  itemIcon: {
    display: 'flex',
    alignItems: 'center',
    width: theme.spacing(2.5),
    minWidth: 'unset',
    marginRight: theme.spacing(1.25),
    color: theme.palette.primary.main,
  },
  verticalBox: {
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    alignContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    width: theme.spacing(35),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingTop: theme.spacing(1),
    wordBreak: 'break-word',
  },
  userSettingAvatar: {
    cursor: 'pointer',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    width: theme.spacing(3),
    height: theme.spacing(3),
    borderWidth: theme.spacing(0.125),
    borderStyle: 'solid',
    borderColor: theme.palette.primary.contrastText,
  },
  userAvatar: {
    minWidth: theme.spacing(7.5),
    minHeight: theme.spacing(7.5),
  },
  userDisplayName: {
    fontSize: theme.spacing(1.75),
    paddingTop: theme.spacing(0.5),
  },
  userDisplayEmail: {
    fontSize: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  viewProfileButton: {
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    '&:hover': {
      color: '#fff !important',
    },
  },
  viewProfileIcon: {
    width: theme.spacing(1.5),
    height: theme.spacing(1.5),
  },
  menuItem: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(1.5),
    fontSize: theme.spacing(2),
    height: theme.spacing(6),
  },
  menuContainer: {
    maxHeight: '90vh',
    overflow: 'auto',
    width: theme.spacing(37.5),
  },
  actionIcon: {
    width: theme.spacing(1.75),
    marginLeft: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  divider: {
    borderColor: 'rgba(0, 17, 65, 0.08)',
  },
}));

export const UserMenu = () => {
  const classes = useStyles();
  const intl = useIntl();
  const anchorRef = useRef<HTMLButtonElement>(null);

  const user = useSelector(fromAuth.selectUser);

  const [menuOpen, setMenuOpen] = useState(false);

  const handleClick = () => {
    setMenuOpen(!menuOpen);
  };

  const closeMenu = (event: React.MouseEvent<EventTarget>) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }
    setMenuOpen(false);
  };

  const handleLogout = () => {
    window.location.replace(logout.path);
  };

  return (
    <div id="layout.header.user_menu" className={classes.root}>
      <Tooltip
        label={!menuOpen ? intl.formatMessage({ id: 'layout.header.user_menu.tooltip' }) : ''}
        placement="bottom"
        size="medium"
        TransitionProps={{ timeout: 0 }}
      >
        <IconButton
          onClick={handleClick}
          ref={anchorRef}
          className={classes.settingButton}
          size="medium"
        >
          <Avatar
            className={classes.userSettingAvatar}
            src={getUserAvatar(user?.avatar)}
          >
            <PersonIcon />
          </Avatar>
        </IconButton>
      </Tooltip>
      <MenuListDecorator
        open={menuOpen}
        anchorEl={anchorRef.current}
        onClickAway={closeMenu}
      >
        <div className={classes.menuContainer}>
          <Box className={classes.verticalBox}>
            <Avatar
              className={classes.userAvatar}
              src={getUserAvatar(user?.avatar)}
            >
              <PersonIcon />
            </Avatar>
            <Box fontWeight="fontWeightBold" className={classes.userDisplayName}>
              {!user?.firstName && !user?.lastName ? user?.email.split('@')[0] : `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim()}
            </Box>
            <Typography className={classes.userDisplayEmail}>
              {user?.email}
            </Typography>
            <Button
              size="small"
              variant="contained"
              color="primary"
              component={RouterLink}
              to={`${process.env.REACT_APP_KATONE_URL}/profile`}
              classes={{ containedSizeSmall: classes.viewProfileButton }}
              target="_blank"
            >
              <FormattedMessage id="layout.header.user_menu.profile.label" />
              &nbsp;&nbsp;
              <ViewProfileIcon className={classes.viewProfileIcon} />
            </Button>
          </Box>
          <Divider className={classes.divider} />
          <MenuItem
            component="a"
            className={classes.menuItem}
            target="_blank"
            rel="noopener noreferrer"
            href={process.env.REACT_APP_KATALON_COMMUNITY_URL}
            onClick={closeMenu}
            divider
          >
            <ListItemAvatar classes={{ root: classes.itemIcon }}>
              <CommunityIcon />
            </ListItemAvatar>
            <FormattedMessage id="layout.header.user_menu.community.label" />
          </MenuItem>
          {/* {config?.serviceCloudEnabled && ( */}
          <MenuItem
            component="a"
            className={classes.menuItem}
            target="_blank"
            href={process.env.REACT_APP_SERVICE_CLOUD_OAUTH_INITIALIZATION_URL}
            onClick={closeMenu}
            divider
          >
            <ListItemAvatar classes={{ root: classes.itemIcon }}>
              <SubmitTicketIcon />
            </ListItemAvatar>
            <FormattedMessage id="layout.header.user_menu.support_case.label" />
          </MenuItem>
          {/* )} */}
          <MenuItem
            className={classes.menuItem}
            onClick={handleLogout}
          >
            <ListItemAvatar classes={{ root: classes.itemIcon }}>
              <SignOutIcon />
            </ListItemAvatar>
            <FormattedMessage id="layout.header.user_menu.sign_out.label" />
          </MenuItem>
        </div>
      </MenuListDecorator>
    </div>
  );
};
